import { alpha } from '@mui/material';
import color from './utils/color';

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

interface TreedisColors {
  blue: {
    main: string;
    lighter: string;
    lightest: string;
  };
  cyan: {
    main: string;
  };
  pink: {
    main: string;
  };
  green: {
    main: string;
  };
}

declare module '@mui/material' {
  interface TypeBackground {
    neutral: string;
    darker: string;
  }

  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    treedis: TreedisColors;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    25: string;
    50: string;
    100: string;
    150: string;
    200: string;
    300: string;
    350: string;
    400: string;
    500: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
    600: string;
    700: string;
    750: string;
    800: string;
    900: string;
  }
}

const primaryColor = '#0E98FE';
const successColor = '#40C053';
const infoColor = '#0494FE';

const PRIMARY = {
  light: color.light(primaryColor),
  main: primaryColor,
  dark: color.dark(primaryColor),
};
const SECONDARY = {
  light: color.light('#344054'),
  main: '#344054',
  dark: color.dark('#344054'),
};
const INFO = {
  light: color.light(infoColor),
  main: infoColor,
  dark: color.dark(infoColor),
};
const SUCCESS = {
  light: color.light(successColor),
  main: successColor,
  dark: color.dark(successColor),
};
const WARNING = {
  light: '#FFF4CD',
  main: '#FAAD39',
  dark: color.dark('#FAAD39'),
};
const ERROR = {
  light: '#FFF1EF',
  main: '#F34848',
  dark: color.dark('#F34848'),
};

const GREY = {
  25: '#FFFFFF',
  50: '#F9FAFB',
  100: '#F2F4F7',
  150: '#DAD9D8',
  200: '#eaecf0',
  300: '#d0d5dd',
  350: '#676767',
  400: '#98A2B3',
  500: '#667085',
  600: '#6B6B6B',
  700: '#344054',
  750: '#808080',
  800: '#363636',
  900: '#101828',
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#101828', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: '#fff' },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  treedis: {
    blue: {
      main: '#2055FF',
      lighter: alpha('#2055FF', 0.8),
      lightest: alpha('#2055FF', 0.1),
    },
    cyan: {
      main: '#39F8EA',
    },
    pink: {
      main: '#FD7BF2',
    },
    green: {
      main: '#B3E6CF',
    },
  },
  divider: GREY[300],
  action: {
    hover: GREY[50],
    selected: GREY[200],
    disabled: GREY[400],
    disabledBackground: GREY[300],
    focus: GREY[300],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[500], disabled: GREY[400] },
    background: { paper: '#fff', default: GREY[300], neutral: GREY[200], darker: GREY[400] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], darker: GREY[800] },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;
